import Component from '@glimmer/component'
import { t } from 'ember-intl'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { AveragesReport } from 'teacher-ui-client/components/reporting/writing-legends/quizzes-completed/averages/report'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import { trackedFunction } from 'reactiveweb/function'
import { toRequestScope, type BaseReport } from 'teacher-ui-client/services/reporting'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import type { NormalizedInterval } from 'date-fns'
import { ChartTooltip } from 'teacher-ui-client/components/reporting/writing-legends/quizzes-completed/chart-tooltip'
import { keepLatest } from 'reactiveweb/keep-latest'
import { use } from 'ember-resources'
import { NoReportValue } from 'teacher-ui-client/components/reporting/no-report-value'
import { array } from '@ember/helper'

export interface ReportData extends BaseReport {
  quizzes_completed_quiz_results: AveragesReportDataForGrade[]
}

export interface AveragesReportDataForGrade {
  grade_position: number
  lessons: Array<{
    attempts: number
    average_score: number
    lesson: number
    student_count: number
  }>
  summary: {
    average_attempts: Nullable<number>
    average_score: Nullable<number>
    scores: Nullable<Record<string, number>>
  }
}

interface ReportLoaderSignature {
  Args: {
    uiScope: UIScope
    period: NormalizedInterval
  }
}

export class ReportLoader extends Component<ReportLoaderSignature> {
  @service reporting!: ReportingService

  fetchReport = trackedFunction(this, async () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const url = this.reporting.buildGravityUrl({
      product: Product.WL,
      requestScope,
      reportName: 'quizzes-completed/quiz-results/averages',
    })
    const query = this.reporting.buildQuery({ period: this.args.period })
    const report = await this.reporting.fetchReport<ReportData>(url, query)
    return report.quizzes_completed_quiz_results.length ? report.quizzes_completed_quiz_results : null
  })

  @use
  latestReport = keepLatest({
    value: () => this.fetchReport.value,
    when: () => this.fetchReport.isPending,
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    {{#each this.latestReport as |averagesReportDataForGrade|}}
      <ContentTile class="relative h-76 {{if this.fetchReport.isPending 'opacity-50'}}">
        <ChartTooltip @text={{t "reporting.tooltips.quizScoresAverages"}} />
        <AveragesReport @reportData={{averagesReportDataForGrade}} @scopeName={{@uiScope.scope}} />
      </ContentTile>
    {{else}}
      <ContentTile class="p-4">
        <NoReportValue @reportStates={{array this.fetchReport}} />
      </ContentTile>
    {{/each}}
  </template>
}
